import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { DndContext } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { clsx } from 'clsx'

import { Upload } from '@buffer-mono/uploader'
import type { Uploader } from '@buffer-mono/uploader'

import { IntegrationImportThumbnail } from '~publish/components/IntegrationImportThumbnail'
import { useCustomSensors } from '~publish/hooks/useCustomSensors'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'

import { AddMediaButton } from './AddMediaButton'
import { MediaPreviewer } from './MediaPreviewer'
import { SortableThumbnail } from './SortableThumbnail'
import { Thumbnail } from './Thumbnail'
import { selectUploaderFiles } from '../state/selectors'
import { reorderUploads } from '../state/slice'

import styles from './MediaManager.module.css'

export const MAX_THUMBNAILS = 10

export const MediaManager = ({
  id,
  containerRef,
  uploader,
  readOnly = false,
}: {
  id: string
  containerRef?: HTMLElement | null
  uploader: Uploader
  /** Show the media without the add, remove, and reorder affordances. */
  readOnly?: boolean
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const [uploadInPreview, setUploadInPreview] = useState<Upload | null>(null)
  const sensors = useCustomSensors()

  const allUploads = useAppSelector((state) => selectUploaderFiles(state, id))
  const completedIds = allUploads.filter(Upload.isCompleted).map((u) => u.id)

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event

      if (over && active.id !== over?.id) {
        dispatch(
          reorderUploads({
            activeId: String(active.id),
            targetId: String(over.id),
          }),
        )
      }
    },
    [dispatch],
  )
  return (
    <div
      className={clsx(styles.mediaManager)}
      id="media-manager"
      data-testid="media-manager"
    >
      <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={completedIds}>
          {allUploads.map((up) =>
            Upload.isCompleted(up) && !readOnly ? (
              <SortableThumbnail key={up.id} id={up.id}>
                <Thumbnail
                  upload={up}
                  openPreview={setUploadInPreview}
                  uploader={uploader}
                />
              </SortableThumbnail>
            ) : (
              <div key={up.id}>
                <Thumbnail
                  upload={up}
                  openPreview={setUploadInPreview}
                  uploader={uploader}
                  readOnly={readOnly}
                />
              </div>
            ),
          )}
        </SortableContext>
      </DndContext>
      {!readOnly && <IntegrationImportThumbnail uploaderId={id} />}
      {allUploads.length < MAX_THUMBNAILS && !readOnly && (
        <AddMediaButton uploader={uploader} />
      )}
      <MediaPreviewer
        upload={uploadInPreview}
        containerRef={containerRef}
        onClose={(): void => setUploadInPreview(null)}
      />
    </div>
  )
}
